import { CurrencyCode, CurrencyConfig } from "@/types/billing/currency";

/**
 * Country to currency mapping for auto-detection based on browser locale
 */
export const COUNTRY_CURRENCY_MAP: Record<string, CurrencyCode> = {
  // USD countries
  US: "USD",

  // NZD countries
  NZ: "NZD",

  // AUD countries
  AU: "AUD",

  // GBP countries
  GB: "GBP",
  UK: "GBP",

  // EUR countries
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  AT: "EUR",
  IE: "EUR",
  PT: "EUR",
  FI: "EUR",
  GR: "EUR",
  LU: "EUR",
  MT: "EUR",
  CY: "EUR",
  SK: "EUR",
  SI: "EUR",
  EE: "EUR",
  LV: "EUR",
  LT: "EUR",
};

export const DEFAULT_CURRENCY: CurrencyCode = "USD";

/**
 * Central pricing configuration for all supported currencies.
 *
 * To update prices:
 * 1. Update the price in Stripe dashboard
 * 2. Update displayPrices here to match
 * 3. Deploy
 *
 * To add a new currency:
 * 1. Create Stripe price for the new currency
 * 2. Add currency to CURRENCIES below
 * 3. Add country mappings to COUNTRY_CURRENCY_MAP above
 * 4. Deploy
 */
export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    stripePriceIds: {
      professional: "price_1SPc0IDIhQJ7n9uB0l669UGg",
      // professional: "price_1K6U7pKAxs3mniwJwKzfyemb",
      phoneLocal: "price_1SwZT0DIhQJ7n9uB05eBMuAY",
      phoneTollFree: "price_1SwZWtDIhQJ7n9uBzistimg5",
      additionalCall: "price_1T4GjyDIhQJ7n9uBxDXB6Lda",
    },
    displayPrices: {
      professional: 99,
      additionalCall: 1,
      phoneLocal: 5,
      phoneTollFree: 9,
      onboardingFee: 499,
    },
  },
  NZD: {
    code: "NZD",
    symbol: "$",
    name: "New Zealand Dollar",
    stripePriceIds: {
      professional: "price_1SwYvIDIhQJ7n9uBtmailk0r",
      // professional: "price_1K6U7pKAxs3mniwJwKzfyemb",
      phoneLocal: "price_1SwZTZDIhQJ7n9uB8Sm1yY8e",
      // phoneLocal: "price_1Js5BcKAxs3mniwJPMKuO4I2",
      phoneTollFree: "price_1SwZXDDIhQJ7n9uBOJODt2q4",
      additionalCall: "price_1T4GkODIhQJ7n9uBsrDH6u6u",
    },
    displayPrices: {
      professional: 169,
      additionalCall: 1.5,
      phoneLocal: 19,
      phoneTollFree: 129,
      onboardingFee: 499,
    },
  },
  AUD: {
    code: "AUD",
    symbol: "$",
    name: "Australian Dollar",
    stripePriceIds: {
      professional: "price_1SwYvgDIhQJ7n9uBG8xSf7nk",
      phoneLocal: "price_1SwZTrDIhQJ7n9uB8mwWncdL",
      phoneTollFree: "price_1SwZXeDIhQJ7n9uBKhtIB1Og",
      additionalCall: "price_1T4GkdDIhQJ7n9uBBlqtLJzK",
    },
    displayPrices: {
      professional: 149,
      additionalCall: 1.5,
      phoneLocal: 15,
      phoneTollFree: 49,
      onboardingFee: 499,
    },
  },
  GBP: {
    code: "GBP",
    symbol: "£",
    name: "British Pound",
    stripePriceIds: {
      professional: "price_1SwYvxDIhQJ7n9uB79ZD2Pno",
      phoneLocal: "price_1SwZU7DIhQJ7n9uBMOCQVIfE",
      phoneTollFree: "price_1SwZXuDIhQJ7n9uBowVAAWMI",
      additionalCall: "price_1T4GkxDIhQJ7n9uBAZ3jqALQ",
    },
    displayPrices: {
      professional: 79,
      additionalCall: 0.8,
      phoneLocal: 5,
      phoneTollFree: 9,
      onboardingFee: 499,
    },
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    name: "Euro",
    stripePriceIds: {
      professional: "price_1SwYwGDIhQJ7n9uBlBLqi5T6",
      phoneLocal: "price_1SwZUODIhQJ7n9uBHbf9BWrt",
      phoneTollFree: "price_1SwZY9DIhQJ7n9uBfzF3o7f0",
      additionalCall: "price_1T4Gl6DIhQJ7n9uBqtPYaDCS",
    },
    displayPrices: {
      professional: 89,
      additionalCall: 0.9,
      phoneLocal: 5,
      phoneTollFree: 9,
      onboardingFee: 499,
    },
  },
};

/**
 * Format a price amount with the appropriate currency symbol
 */
export const formatPrice = (amount: number, currency: CurrencyCode): string => {
  const config = CURRENCIES[currency];

  // Handle decimal amounts appropriately
  const formattedAmount = Number.isInteger(amount) ? amount.toLocaleString() : amount.toFixed(2);

  return `${config.symbol}${formattedAmount}`;
};

/**
 * Get currency config, with fallback to default
 */
export const getCurrencyConfig = (code: CurrencyCode): CurrencyConfig => {
  return CURRENCIES[code] || CURRENCIES[DEFAULT_CURRENCY];
};

/**
 * Get all available currency codes
 */
export const getAvailableCurrencies = (): CurrencyCode[] => {
  return Object.keys(CURRENCIES) as CurrencyCode[];
};
